import { KeyboardEvent } from 'react';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { ListRow, NakedButton, PopoverDialog, TextField, usePopoverDialog } from '@frontend/design-system';
import { GenericFieldProps } from '../types';
import { convertValueToPixels } from '../utils/convert-value-to-pixels';

type Props = GenericFieldProps & {
  options: string[];
  defaultValue?: string;
};

export const TextDropdownField = ({ disabled, options, defaultValue, ...fieldProps }: Props) => {
  const { getDialogProps, getTriggerProps, open, isOpen, close } = usePopoverDialog({
    placement: 'bottom-start',
    initialOffset: {
      x: -77,
      y: 0,
    },
  });

  const handleChange = (value: string) => {
    if (disabled) return;
    fieldProps.onChange({ ...fieldProps, value: convertValueToPixels(value, defaultValue) });
  };

  const handleArrowKeys = (e: KeyboardEvent<HTMLInputElement>) => {
    if (disabled || (e.key !== 'ArrowUp' && e.key !== 'ArrowDown')) return;

    const rawValue = parseInt(fieldProps.value, 10);
    const numericalValue = isNaN(rawValue) ? 0 : Math.max(0, rawValue);

    if (e.key === 'ArrowUp') {
      handleChange((numericalValue + 1).toString());
    } else if (e.key === 'ArrowDown') {
      handleChange((numericalValue - 1).toString());
    }
  };

  // TODO: Change the style of this dropdown's checkmark to match the Location Picker's style
  return (
    <>
      <TextField
        {...fieldProps}
        label=''
        css={{
          alignItems: 'stretch',
          height: theme.spacing(4),
          padding: 0,
          width: theme.spacing(11),

          '& input': {
            flex: 1,
            padding: theme.spacing(0, 1),
            marginRight: '0 !important',
          },
        }}
        endAdornment={
          <NakedButton
            {...getTriggerProps()}
            css={{
              alignSelf: 'stretch',
              padding: theme.spacing(0, 1),
            }}
            onClick={() => (isOpen ? close() : open())}
          >
            <Icon
              name='caret-down-tiny'
              css={[
                {
                  transition: 'transform 0.2s',
                },
                isOpen && {
                  transform: 'rotate(180deg)',
                },
              ]}
            />
          </NakedButton>
        }
        onBlur={(e) => {
          if (e?.target.value !== undefined) handleChange(e.target.value);
        }}
        onKeyDown={handleArrowKeys}
        disabled={disabled}
      />
      <PopoverDialog
        {...getDialogProps()}
        css={{
          width: theme.spacing(13),
          padding: 0,
        }}
      >
        {options.map((option) => (
          <ListRow
            key={option}
            css={{
              borderBottom: 'unset',
              padding: theme.spacing(1, 2),
              cursor: 'pointer',
              '&:hover, &:focus': {
                background: theme.colors.neutral5,
              },
            }}
            onClick={() => {
              handleChange(option);
              close();
            }}
          >
            {option}
            {fieldProps.value === option && (
              <Icon
                name='check'
                css={{
                  marginLeft: theme.spacing(2),
                  color: theme.colors.primary50,
                }}
              />
            )}
          </ListRow>
        ))}
      </PopoverDialog>
    </>
  );
};
